import React, { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { ILegalDocument } from '../../services/exports/Interfaces';

interface Props {
  bundles: Record<string, ComponentType>;
  legalPack: string | undefined;
  generated?: ILegalDocument;
  isPending?: boolean;
}

/**
 * Typography for the generated document.
 *
 * The server-rendered fragment carries no classes and no stylesheet of its own — the
 * backend is forbidden from emitting `<style>`, so a body arrives as bare `section`,
 * `h1`, `h2`, `p` and `dl`. Without this it renders in the browser's defaults, which
 * next to the hand-styled jurisdiction bundles reads as a broken page.
 *
 * Applied from here as descendant selectors rather than as classes in the Blade, so the
 * backend stays unaware of Tailwind and the same fragment can serve a second frontend.
 *
 * `overflow-wrap: anywhere` rather than `break-words`: the fragment prints emails, VAT
 * numbers and the shop URL as unbroken strings inside a grid, and only `anywhere` lowers
 * their min-content width — with `break-word` a long address still widens the grid track
 * past a phone's viewport.
 */
const GENERATED_PROSE = [
  'flex flex-col w-screen lg:py-xl py-lg lg:px-xl px-md [overflow-wrap:anywhere]',
  '[&_section]:max-w-3xl',
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-md',
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-lg [&_h2]:mb-sm',
  '[&_p]:mb-md [&_p]:leading-relaxed',
  '[&_dl]:mb-md [&_dl]:grid [&_dl]:gap-x-md [&_dl]:gap-y-2xs',
  'lg:[&_dl]:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]',
  '[&_dt]:font-semibold',
  '[&_dd]:mb-xs lg:[&_dd]:mb-0',
  '[&_ul]:list-disc [&_ul]:pl-lg [&_ul]:mb-md',
  '[&_[data-legal-draft]]:bg-brand-primary/10 [&_[data-legal-draft]]:text-brand-text-primary',
  '[&_[data-legal-draft]]:text-sm [&_[data-legal-draft]]:px-md [&_[data-legal-draft]]:py-sm [&_[data-legal-draft]]:mb-lg',
].join(' ');

// company.region.legal_pack selects which jurisdiction's legal document set to render. Only
// `de`/`gb` bundles exist today — any other pack (or a missing region) falls through to the
// English bundle with a visible notice, rather than silently rendering the wrong country's
// legal text.
//
// `generated` is the restaurant's own document, when the backend produced one. It arrives
// already rendered because the copy has a single source of truth server-side: duplicating
// the legal prose here would mean a second copy to keep in step the moment another
// frontend needs it. Everything in it is escaped server-side and these routes are noindex
// (see MetaInfo), so `dangerouslySetInnerHTML` is the intended mechanism rather than a
// shortcut.
//
// `isPending` is the one case that must NOT fall through to the bundle: a document is
// coming, and showing the platform's legal text in the meantime is the flash this feature
// exists to remove. Everything else — flag off, incomplete restaurant profile, failed
// request — renders the bundle, exactly as this app did before the endpoint existed.
//
// Named `generated` rather than `document` on purpose: a prop called `document` shadows
// window.document inside this component, so any future DOM access here would silently
// resolve to the prop instead.
export default function LegalPage({ bundles, legalPack, generated, isPending = false }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:Legal' });

  if (generated?.source === 'generated' && generated.html) {
    return (
      <div
        className={GENERATED_PROSE}
        lang={generated.locale ?? undefined}
        dangerouslySetInnerHTML={{ __html: generated.html }}
      />
    );
  }

  if (isPending) {
    // A skeleton rather than the app's Lottie indicator: this stands in for a page of
    // text, and the indicator pulls a device context that a legal route does not need.
    return (
      <div className={`${GENERATED_PROSE} animate-pulse`} data-test="legal-document-pending">
        <div className="bg-brand-primary/10 h-8 w-2/3 rounded mb-lg" />
        {[...Array(6)].map((_, index) => (
          <div key={index} className="bg-brand-primary/10 h-4 w-full rounded mb-sm" />
        ))}
      </div>
    );
  }

  const Bundle = legalPack ? bundles[legalPack] : undefined;

  if (Bundle) {
    return <Bundle />;
  }

  const Fallback = bundles.gb ?? Object.values(bundles)[0];

  return (
    <>
      <div className="bg-brand-primary/10 text-brand-text-primary text-sm px-md py-sm">{t('not_localised')}</div>
      <Fallback />
    </>
  );
}
